import type { OrderDirection, Status } from '~api/som_search';

import { NavLink } from 'react-router-dom';

import { Button, InteractiveTable, PageTitle, PageWrapper, PaginationProvider } from '@ingrid/luterkort-react';

import { RoleGuard } from '~src/components/RoleGuard/RoleGuard';
import { useTranslation } from '~src/i18n/use-translation';
import { useDebounce } from '~src/lib/hooks/useDebounce';
import { usePagination } from '~src/main/hooks/usePagination';
import { AppRoute } from '~src/main/routes';
import { stringifyDateTimeRange } from '~src/utils/date';

import { stringifyDateRange } from '../../../utils/date';
import { useShipmentList, useShipmentsCountQuery } from '../queries';
import { Filters } from './filters/filters';
import { Tags } from './filters/tags';
import { useShipmentListFilter } from './filters/useShipmentListFilter';
import { GroupStatus } from './groups';
import { ListTable } from './list-table';
import { ListTableBar } from './list-table-bar';

const getShipmentIdFilter = (filter: ReturnType<typeof useShipmentListFilter>['filtering']) => {
  if (filter.excludedPrintIds) {
    return atob(filter.excludedPrintIds).split(',');
  }

  if (filter.excludedBookIds) {
    return atob(filter.excludedBookIds).split(',');
  }

  if (filter.excludedSchedulePickupIds) {
    return atob(filter.excludedSchedulePickupIds).split(',');
  }

  return [];
};

export function Page() {
  const { filtering, advancedFiltering } = useShipmentListFilter();
  const { pageIndex, pageSize, direction, setPageIndex, setPageSize } = usePagination();
  const { t } = useTranslation();

  const query = {
    statuses: filtering.group !== 'ALL' ? GroupStatus[filtering.group] : filtering.statuses,
    shipment_ids: getShipmentIdFilter(filtering),
    shipping_date_range: stringifyDateRange(advancedFiltering.shippingDateRange),
    created_at_range: stringifyDateTimeRange(advancedFiltering.createdAtRange),
    address_from_countries: advancedFiltering.originCountries,
    address_to_countries: advancedFiltering.deliveryCountries,
    direction_types: advancedFiltering.directionTypes,
    id: useDebounce(filtering.search, 300),
    shipping_methods: filtering.carriers,
  };

  const shipmentsQuery = useShipmentList({
    filtering: {
      ...query,
      statuses: query.statuses as Status[],
    },
    order_by_direction: direction.toUpperCase() as OrderDirection,
    pageIndex: pageIndex - 1,
    pageSize: pageSize,
  });

  const { data: shipmentsCountData } = useShipmentsCountQuery({
    ...query,
    statuses: filtering.statuses as Status[],
  });

  return (
    <InteractiveTable.Provider allowHighlight keepInactive>
      <PaginationProvider
        onPageSizeChange={setPageSize}
        onPageNoChange={setPageIndex}
        pageSize={pageSize}
        pageNo={pageIndex}
      >
        <PageWrapper>
          <PageTitle.Wrapper>
            <PageTitle.Stack>
              <PageTitle.Title>{t('common.shipments')}</PageTitle.Title>
            </PageTitle.Stack>

            <RoleGuard roles={['transport:edit']} withTooltip>
              <Button as={NavLink} to={AppRoute.CREATE}>
                {t('common.create')}
              </Button>
            </RoleGuard>
          </PageTitle.Wrapper>

          <Filters />
          <Tags />

          <section style={{ backgroundColor: 'inherit' }}>
            <ListTableBar shipmentsCountData={shipmentsCountData} />

            <ListTable
              isEmpty={!shipmentsQuery.isLoading && shipmentsQuery.data.length === 0}
              isLoading={shipmentsQuery.isLoading}
              shipments={shipmentsQuery.data}
              loadRowCount={pageSize}
            />
          </section>
        </PageWrapper>
      </PaginationProvider>
    </InteractiveTable.Provider>
  );
}
